import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import SeoStructuredData from '@/components/feature/SeoStructuredData';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, getFeaturedArticles, getHotArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Home() {
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);
  const featuredArticles = getFeaturedArticles();
  const hotArticles = getHotArticles();
  const latestArticles = [...articles].sort(
    (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
  );

  const heroArticle = featuredArticles[currentHeroSlide] || featuredArticles[0];

  return (
    <>
      <ScrollToTop />
      <SeoStructuredData type="WebSite" />
      <SeoStructuredData type="Organization" />
      <SeoStructuredData
        type="WebPage"
        pageTitle="久草社区-热门内容聚合与用户交流平台"
        pageDescription="久草社区是深受用户喜爱的在线内容聚合与互动交流平台，汇聚海量精选资源、活跃社区讨论与多元兴趣圈子。"
        pageUrl="https://jiucao.community"
        breadcrumbItems={[{ name: '首页', url: 'https://jiucao.community' }]}
      />

      <Navbar />

      <main>
        <section className="relative pt-16">
          <div className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={heroArticle?.coverImage || articles[0].coverImage}
                alt={heroArticle?.title || '久草社区'}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="absolute inset-0 flex items-center">
              <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                      {heroArticle?.category}
                    </span>
                    {heroArticle?.isHot && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                        热门推荐
                      </span>
                    )}
                  </div>
                  <Link to={`/article/${heroArticle?.id}`}>
                    <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4 leading-tight hover:text-accent-300 transition-colors">
                      {heroArticle?.title}
                    </h1>
                  </Link>
                  <p className="text-base text-white/80 leading-relaxed mb-6 line-clamp-2 max-w-xl">
                    {heroArticle?.excerpt}
                  </p>
                  <div className="flex items-center gap-6 text-sm text-white/70">
                    <span className="flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-full overflow-hidden bg-white/20">
                        <img src={heroArticle?.authorAvatar} alt={heroArticle?.author} className="w-full h-full object-cover" />
                      </div>
                      {heroArticle?.author}
                    </span>
                    <span>{heroArticle?.publishDate}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {heroArticle?.readCount && heroArticle.readCount >= 10000
                        ? `${(heroArticle.readCount / 10000).toFixed(1)}万阅读`
                        : `${heroArticle?.readCount}阅读`}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
              {featuredArticles.slice(0, 4).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentHeroSlide(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    index === currentHeroSlide
                      ? 'bg-white w-8'
                      : 'bg-white/40 hover:bg-white/70'
                  }`}
                  aria-label={`切换到第${index + 1}张`}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              分类浏览
            </h2>
            <Link
              to="/category/tech"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors flex items-center gap-1"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center gap-3 p-4 md:p-5 rounded-xl bg-background-50 border border-background-200/60 hover:border-accent-200 hover:bg-accent-50/30 transition-all duration-300"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                  <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-accent-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{cat.articleCount}篇</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="bg-background-100/70 py-10 md:py-14">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                精选推荐
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="featured" />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  最新文章
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {latestArticles.slice(0, 6).map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-background-50 rounded-xl border border-background-200/60 p-5 mb-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-1 h-5 rounded-full bg-accent-500"></div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">
                      热门文章
                    </h3>
                  </div>
                  <div className="divide-y divide-background-200/40">
                    {hotArticles.slice(0, 6).map((article) => (
                      <ArticleCard key={article.id} article={article} variant="compact" />
                    ))}
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/60 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">
                      热门标签
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {['人工智能', '成都美食', '健身', '学习方法', '日本旅行', '古典音乐', '职场', '极简主义', '数码评测', '心理健康'].map(
                      (tag) => (
                        <Link
                          key={tag}
                          to="/category/tech"
                          className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
                        >
                          {tag}
                        </Link>
                      )
                    )}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="bg-background-100/70 py-10 md:py-14">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
              加入久草社区
            </h2>
            <p className="text-foreground-600 mb-6 max-w-lg mx-auto">
              汇聚海量精选资源，活跃社区讨论，多元兴趣圈子。发现优质内容，结交志同道合的朋友。
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/category/tech"
                className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                开始浏览
              </Link>
              <Link
                to="/about"
                className="px-6 py-2.5 rounded-full border border-foreground-300 text-foreground-700 font-medium text-sm hover:border-primary-400 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}